import { useState } from "react";
import { ApiError, api, type CasoDetalle } from "@/lib/api";
import { canEdit, useAuth } from "@/lib/auth";
import Modal from "@/ui/Modal";
import Toggle from "@/ui/Toggle";
import { useToast } from "@/ui/Toast";
import { useCommitOnBlur } from "@/lib/useCommitOnBlur";

const HITOS = [
  { key: "fecha_formulario_cliente", label: "Formulario del cliente" },
  { key: "fecha_recepcion_oficina", label: "Recepción en oficina" },
  { key: "fecha_envio_proveedor", label: "Envío al proveedor" },
  { key: "fecha_retorno_proveedor", label: "Retorno del proveedor" },
  { key: "fecha_devolucion_cliente", label: "Devolución al cliente" },
  { key: "fecha_reemplazo_garantia", label: "Reemplazo por garantía" },
] as const;

type HitoKey = typeof HITOS[number]["key"];

export default function PanelHitos({
  caso,
  onChanged,
}: {
  caso: CasoDetalle;
  onChanged: (next: CasoDetalle) => void;
}) {
  const me = useAuth((s) => s.user);
  const toast = useToast();
  // Caso cerrado = read-only. Mantenemos los inputs visibles para
  // auditar las fechas pero sin permitir edicion.
  const editable = canEdit(me) && caso.estado !== "Cerrado";

  // "Fue garantía" + "Seguimiento RMA" solo tienen sentido si hay equipo que
  // traquear (IMEIs). Seguro: si el caso YA tiene datos capturados (garantía o
  // alguna fecha RMA), se muestran aunque ya no tenga IMEIs — no se esconde lo
  // que alguien ya llenó. Un ticket de consulta sin equipo no los ve.
  const mostrarGarantiaRma =
    caso.imeis.length > 0 ||
    caso.fue_garantia ||
    HITOS.some((h) => Boolean(caso[h.key as HitoKey]));

  // El subgrupo "Seguimiento RMA" agrupa las 6 fechas exclusivas del
  // flow de RMA (formulario_cliente, recepcion, envio/retorno proveedor,
  // devolucion, reemplazo). Si ninguna esta llena, el subgrupo aun no
  // aplica — empieza colapsado. El user puede expandirlo manual.
  const rmaTieneDatos = HITOS.some((h) => caso[h.key as HitoKey] !== null);
  const [rmaAbierto, setRmaAbierto] = useState<boolean>(rmaTieneDatos);

  // Modal "motivo" al marcar reemplazo por garantia.
  // Cuando el usuario pasa fecha_reemplazo_garantia de null a fecha,
  // primero pedimos el motivo. Solo confirmando se hace el PATCH + nota.
  const [motivoOpen, setMotivoOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [pendingFecha, setPendingFecha] = useState<string | null>(null);
  const [savingMotivo, setSavingMotivo] = useState(false);

  async function patchField(field: string, value: string | boolean | null) {
    const updated = await api.patch<CasoDetalle>(`/api/casos/${caso.id}`, {
      [field]: value,
    });
    onChanged(updated);
    return updated;
  }

  async function update(field: string, value: string | boolean | null) {
    // Caso especial: marcar reemplazo por garantia (fecha_reemplazo_garantia
    // de null a una fecha) → pide motivo en modal antes de guardar.
    if (
      field === "fecha_reemplazo_garantia" &&
      value &&
      typeof value === "string" &&
      !caso.fecha_reemplazo_garantia
    ) {
      setPendingFecha(value);
      setMotivo("");
      setMotivoOpen(true);
      return;
    }

    try {
      const updated = await patchField(field, value);
      if (updated.estado !== caso.estado) {
        toast.info(
          `El caso pasó a "${updated.estado}"`,
          "Lo moví automáticamente por la fecha. Revisa Equipos y Recordatorios; puedes ajustarlo a mano.",
        );
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo actualizar", msg);
    }
  }

  async function confirmarReemplazo() {
    if (!pendingFecha) return;
    setSavingMotivo(true);
    try {
      await patchField("fecha_reemplazo_garantia", pendingFecha);
      // Si tambien marca "Fue garantia" como true (logico al registrar reemplazo)
      if (!caso.fue_garantia) {
        await patchField("fue_garantia", true);
      }
      // Crea nota con el motivo (si lo hubo). Tag "garantia" + "reemplazo".
      const motivoTrim = motivo.trim();
      if (motivoTrim) {
        await api.post(`/api/casos/${caso.id}/notas`, {
          texto: `Reemplazo por garantia (${pendingFecha}): ${motivoTrim}`,
          imei_id: null,
        });
      }
      toast.success("Reemplazo por garantia registrado");
      setMotivoOpen(false);
      setMotivo("");
      setPendingFecha(null);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo registrar", msg);
    } finally {
      setSavingMotivo(false);
    }
  }

  function cancelarReemplazo() {
    setMotivoOpen(false);
    setMotivo("");
    setPendingFecha(null);
  }

  // Cuantas fechas RMA estan llenas — para el contador del subgrupo.
  const rmaLlenas = HITOS.filter(
    (h) => caso[h.key as HitoKey] !== null,
  ).length;
  const rmaTotal = HITOS.length;

  // Días desde una fecha "YYYY-MM-DD" (null si no hay o es futura).
  const diasDesde = (f: string | null): number | null => {
    if (!f) return null;
    const d = Math.floor((Date.now() - new Date(f + "T00:00:00").getTime()) / 86_400_000);
    return d >= 0 ? d : null;
  };

  // Equipo "contigo": recibido y aún no devuelto, y no está fuera con el
  // proveedor (envío sin retorno). Ancla = retorno si volvió, si no recepción.
  const equipoContigo =
    !!caso.fecha_recepcion_oficina &&
    !caso.fecha_devolucion_cliente &&
    (!caso.fecha_envio_proveedor || !!caso.fecha_retorno_proveedor);
  const diasContigo = diasDesde(caso.fecha_retorno_proveedor || caso.fecha_recepcion_oficina);

  // Fechas RMA fuera de orden cronológico (aviso suave, no bloquea). Garantía
  // queda aparte (no es parte de la cadena).
  const fechasDesordenadas = (() => {
    const cadena = [
      caso.fecha_reporte_cliente,
      caso.fecha_formulario_cliente,
      caso.fecha_recepcion_oficina,
      caso.fecha_envio_proveedor,
      caso.fecha_retorno_proveedor,
      caso.fecha_devolucion_cliente,
    ].filter(Boolean) as string[];
    for (let i = 1; i < cadena.length; i++) if (cadena[i] < cadena[i - 1]) return true;
    return false;
  })();

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-xs uppercase tracking-wide text-zinc-400">Fechas del caso</h3>
        {equipoContigo && diasContigo != null && (
          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300">
            📦 Equipo contigo · {diasContigo} d
          </span>
        )}
      </div>

      {fechasDesordenadas && (
        <p className="text-[11px] text-amber-300 border border-amber-500/30 bg-amber-500/5 rounded px-2 py-1">
          ⚠ Algunas fechas no van en orden cronológico — revísalas.
        </p>
      )}

      {/* Reporte del cliente — el ancla del SLA, siempre arriba. */}
      <Hito
        label="Reporte del cliente"
        value={caso.fecha_reporte_cliente}
        editable={editable}
        highlight
        onChange={(v) => update("fecha_reporte_cliente", v)}
      />

      {/* "Fue garantía" + "Seguimiento RMA": solo si hay equipo que traquear
          (IMEIs) o ya hay datos capturados — ver mostrarGarantiaRma arriba. */}
      {mostrarGarantiaRma && (
      <div className="border-t border-ink-700 pt-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setRmaAbierto((v) => !v)}
            className="text-xs uppercase tracking-wide text-zinc-500 hover:text-zinc-300 transition flex items-center gap-1.5"
          >
            <span>{rmaAbierto ? "▾" : "▸"}</span>
            Seguimiento RMA
            <span className="normal-case tracking-normal text-zinc-600">
              {rmaLlenas === 0
                ? "(sin iniciar)"
                : `(${rmaLlenas}/${rmaTotal} fechas)`}
            </span>
          </button>
          <Toggle
            checked={caso.fue_garantia}
            disabled={!editable}
            onChange={(v) => update("fue_garantia", v)}
            label="Fue garantía"
          />
        </div>

        {rmaAbierto && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
            {HITOS.map((h) => (
              <Hito
                key={h.key}
                label={h.label}
                value={caso[h.key as HitoKey]}
                editable={editable}
                onChange={(v) => update(h.key, v)}
              />
            ))}
          </div>
        )}
      </div>
      )}

      <Modal
        open={motivoOpen}
        onOpenChange={(v) => !v && cancelarReemplazo()}
        title="Reemplazo por garantía"
        description={
          pendingFecha
            ? `Vas a registrar el reemplazo con fecha ${pendingFecha}. Anota el motivo o comentario final — queda como nota del caso.`
            : "Anota el motivo del reemplazo."
        }
        size="md"
        footer={
          <>
            <button onClick={cancelarReemplazo} className="btn-ghost">
              Cancelar
            </button>
            <button
              onClick={confirmarReemplazo}
              disabled={savingMotivo || !motivo.trim()}
              className="btn-primary disabled:opacity-50"
            >
              {savingMotivo ? "Guardando..." : "Registrar reemplazo"}
            </button>
          </>
        }
      >
        <span className="text-xs uppercase tracking-wide text-zinc-400">
          Motivo / comentario
        </span>
        <textarea
          autoFocus
          className="input mt-1 min-h-[120px]"
          placeholder="ej. Falla de hardware confirmada por el proveedor; sustituyeron el equipo por uno nuevo bajo garantía. Número de serie nuevo: 12345678."
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
        <p className="text-xs text-zinc-500 mt-1">
          También marca <strong>Fue garantía</strong> si no estaba.
        </p>
      </Modal>
    </div>
  );
}

function Hito({
  label,
  value,
  editable,
  highlight,
  onChange,
}: {
  label: string;
  value: string | null;
  editable: boolean;
  highlight?: boolean;
  onChange: (v: string | null) => void;
}) {
  // Fecha que solo guarda al salir del campo (blur) o con Enter, no en cada
  // paso del selector nativo. Ver lib/useCommitOnBlur.
  const campo = useCommitOnBlur(value ?? "", (v) => onChange(v || null));

  return (
    <div>
      <label
        className={`block text-[11px] uppercase tracking-wide mb-1 ${
          highlight ? "text-accent-400" : "text-zinc-500"
        }`}
      >
        {label}
      </label>
      <input
        type="date"
        className="input text-xs py-1 w-full sm:max-w-[200px]"
        disabled={!editable}
        {...campo}
      />
    </div>
  );
}
